import type { OrganizationType } from '@kanbu/schema';
import { useLingui } from '@lingui/react/macro';
import { createFileRoute } from '@tanstack/react-router';
import { getQueryKey } from '@trpc/react-query';
import { useMemo, useState } from 'react';

import { TablePage } from '@/components/page/TablePage';
import { MineOnlyToggle } from '@/components/partner/MineOnlyToggle';
import { useOrganizationsCols } from '@/features/organizations';
import { useDetailRowHandler } from '@/hooks/useDetailRowHandler';
import { useTableQuery } from '@/hooks/useTableQuery';
import { partnerOrSuperAdminGuard } from '@/lib/routerUtils';
import { trpc, trpcClient } from '@/services/trpc';

export const Route = createFileRoute('/_baseLayout/organizations/')({
  component: OrganizationsPage,
  beforeLoad: async () => partnerOrSuperAdminGuard(),
});

export function OrganizationsPage() {
  const { t } = useLingui();
  const columns = useOrganizationsCols();
  const [mineOnly, setMineOnly] = useState(false);

  const { data: partnerContext } = trpc.partnerLinks.myContext.useQuery();

  const mineWhere = useMemo(
    () =>
      mineOnly
        ? { id: { $in: partnerContext?.myClientOrganizationIds ?? [] } }
        : undefined,
    [mineOnly, partnerContext?.myClientOrganizationIds],
  );

  const handleQuery = useTableQuery(trpcClient.organizations.findAll.query, {
    where: mineWhere,
  });
  const handleRow = useDetailRowHandler<OrganizationType>(row => ({
    to: '/organizations/$organizationId/edit',
    params: {
      organizationId: row.original.id,
    },
  }));

  return (
    <TablePage
      title={t`Organizations`}
      breadcrumbs={[{ label: t`Organizations`, to: '/organizations' }]}
      createButton={{
        to: '/organizations/create',
        label: t`Create organization`,
      }}
      filters={
        partnerContext?.isPartnerCapable ? (
          <MineOnlyToggle value={mineOnly} onChange={setMineOnly} />
        ) : undefined
      }
      columns={columns}
      queryKey={[...getQueryKey(trpc.organizations.findAll), { mineOnly }]}
      onQueryFn={handleQuery}
      onRow={handleRow}
    />
  );
}
